import type { AccountInfo, AuthenticationResult, SilentRequest } from '@azure/msal-browser';
import { surfaceConfig, validateReturnPath, type AppSurface } from '../appSurface';

const AUTH_GENERATION_KEY = 'valentine:auth-generation';
const BRIDGE_LEASE_KEY = 'valentine:microsoft-bridge-lease';
const BRIDGE_LOCK_NAME = 'valentine:microsoft-bridge-renewal';
const BRIDGE_CHANNEL_NAME = 'valentine-auth';
const LEASE_MS = 30_000;
const FETCH_TIMEOUT_MS = 15_000;

interface BridgeMsalClient {
  getActiveAccount(): AccountInfo | null;
  acquireTokenSilent(request: SilentRequest): Promise<AuthenticationResult>;
}

interface CoordinatorOptions {
  onRemoteLogout: () => void;
  onInteractionRequired: () => void;
}

interface LeaseRecord {
  owner: string;
  expiresAt: number;
}

function safeReadGeneration(): number {
  try {
    const parsed = Number.parseInt(localStorage.getItem(AUTH_GENERATION_KEY) ?? '0', 10);
    return Number.isFinite(parsed) ? parsed : 0;
  } catch {
    return 0;
  }
}

function safeWriteGeneration(generation: number) {
  try { localStorage.setItem(AUTH_GENERATION_KEY, String(generation)); } catch { /* storage unavailable */ }
}

export function invalidateLocalAuthGeneration(): number {
  const generation = safeReadGeneration() + 1;
  safeWriteGeneration(generation);
  return generation;
}

export function resolveSafeAuthReturnPath(surface: AppSurface, raw: string): string {
  return validateReturnPath(raw, surface) ?? surfaceConfig(surface).basename;
}

function readLease(): LeaseRecord | null {
  try {
    const value = JSON.parse(localStorage.getItem(BRIDGE_LEASE_KEY) ?? 'null') as LeaseRecord | null;
    return value && typeof value.owner === 'string' && typeof value.expiresAt === 'number' ? value : null;
  } catch {
    return null;
  }
}

function randomOwner(): string {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
}

function sleep(ms: number) {
  return new Promise<void>(resolve => setTimeout(resolve, ms));
}

async function boundedFetch(
  input: RequestInfo | URL,
  init: RequestInit,
  controller = new AbortController(),
): Promise<Response> {
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  try {
    return await fetch(input, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

function interactionRequired(error: unknown): boolean {
  const candidate = error as { name?: string; errorCode?: string } | null;
  return candidate?.name === 'InteractionRequiredAuthError'
    || candidate?.errorCode === 'interaction_required'
    || candidate?.errorCode === 'consent_required'
    || candidate?.errorCode === 'login_required';
}

async function withLocalStorageLease<T>(operation: () => Promise<T>): Promise<T | false> {
  const owner = randomOwner();
  const deadline = Date.now() + LEASE_MS;
  while (Date.now() < deadline) {
    const now = Date.now();
    const current = readLease();
    if (!current || current.expiresAt <= now || current.owner === owner) {
      try {
        localStorage.setItem(BRIDGE_LEASE_KEY, JSON.stringify({ owner, expiresAt: now + LEASE_MS }));
        if (readLease()?.owner === owner) {
          try {
            return await operation();
          } finally {
            if (readLease()?.owner === owner) localStorage.removeItem(BRIDGE_LEASE_KEY);
          }
        }
      } catch {
        // Storage can be disabled. In that case, same-tab single-flight still
        // protects this tab and the operation is safer than failing auth.
        return operation();
      }
    }
    await sleep(75 + Math.floor(Math.random() * 75));
  }
  return false;
}

async function withCrossTabLock<T>(operation: () => Promise<T>): Promise<T | false> {
  if (navigator.locks?.request) {
    return navigator.locks.request(BRIDGE_LOCK_NAME, { mode: 'exclusive' }, operation);
  }
  return withLocalStorageLease(operation);
}

export class MicrosoftBridgeCoordinator {
  private renewal: Promise<boolean> | null = null;
  private abortController: AbortController | null = null;
  private channel: BroadcastChannel | null = null;
  private disposed = false;
  private logoutStarted = false;
  private readonly msal: BridgeMsalClient;
  private readonly options: CoordinatorOptions;

  constructor(
    msal: BridgeMsalClient,
    options: CoordinatorOptions,
  ) {
    this.msal = msal;
    this.options = options;
    if ('BroadcastChannel' in globalThis) {
      this.channel = new BroadcastChannel(BRIDGE_CHANNEL_NAME);
      this.channel.addEventListener('message', this.onChannelMessage);
    }
    window.addEventListener('storage', this.onStorage);
  }

  async probe(): Promise<boolean> {
    try {
      const response = await boundedFetch('/api/auth/me', {
        method: 'GET',
        credentials: 'include',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return false;
      const body = await response.json() as { session_auth_provider?: string };
      return body.session_auth_provider === 'microsoft-bridge';
    } catch {
      return false;
    }
  }

  renew(): Promise<boolean> {
    if (this.disposed || this.logoutStarted) return Promise.resolve(false);
    if (!this.renewal) {
      this.renewal = this.runRenewal().finally(() => { this.renewal = null; });
    }
    return this.renewal;
  }

  private async runRenewal(): Promise<boolean> {
    const generation = safeReadGeneration();
    const result = await withCrossTabLock(async () => {
      // Another tab may have completed renewal while this one waited.
      if (this.disposed || this.logoutStarted || generation !== safeReadGeneration()) return false;
      if (await this.probe()) {
        return !this.disposed && !this.logoutStarted && generation === safeReadGeneration();
      }
      if (this.disposed || this.logoutStarted || generation !== safeReadGeneration()) return false;

      const account = this.msal.getActiveAccount();
      if (!account) return false;

      try {
        const token = await this.msal.acquireTokenSilent({
          scopes: ['openid', 'profile', 'email'],
          account,
        });
        if (
          !token.idToken
          || this.disposed
          || this.logoutStarted
          || generation !== safeReadGeneration()
        ) return false;

        this.abortController = new AbortController();
        const { isStandaloneDisplayMode } = await import('../utils/displayMode');
        const response = await boundedFetch('/api/auth/session/bridge', {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token.idToken}`,
          },
          body: JSON.stringify({
            display_mode: isStandaloneDisplayMode() ? 'standalone' : 'browser',
            explicit_reauth: false,
          }),
        }, this.abortController);
        const generationStillCurrent = generation === safeReadGeneration();
        if (!generationStillCurrent) {
          await this.revokeCookie([]);
          return false;
        }
        return response.ok;
      } catch (error) {
        if (interactionRequired(error) && generation === safeReadGeneration()) {
          this.options.onInteractionRequired();
        }
        return false;
      } finally {
        this.abortController = null;
      }
    });
    return result === false ? false : result;
  }

  /** Synchronously invalidate this auth generation before any logout awaits. */
  beginLogout(): void {
    if (this.logoutStarted) return;
    this.logoutStarted = true;
    const generation = invalidateLocalAuthGeneration();
    this.channel?.postMessage({ type: 'logout', generation });
    this.abortController?.abort();
  }

  async finishLogout(pushEndpoints: string[]): Promise<void> {
    this.beginLogout();
    if (this.renewal) await this.renewal.catch(() => false);
    await this.revokeCookie(pushEndpoints);
  }

  /** Convenience for non-React callers. */
  async logout(pushEndpoints: string[]): Promise<void> {
    this.beginLogout();
    await this.finishLogout(pushEndpoints);
  }

  dispose() {
    this.disposed = true;
    this.abortController?.abort();
    this.channel?.removeEventListener('message', this.onChannelMessage);
    this.channel?.close();
    window.removeEventListener('storage', this.onStorage);
  }

  private readonly revokeCookie = async (pushEndpoints: string[]) => {
    try {
      await boundedFetch('/api/auth/native/logout', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          push_endpoints: pushEndpoints,
          push_endpoints_resolved: true,
        }),
      });
    } catch {
      // Microsoft logout must continue even while Valentine is offline.
    }
  };

  private readonly handleRemoteLogout = () => {
    this.logoutStarted = true;
    this.abortController?.abort();
    this.options.onRemoteLogout();
  };

  private readonly onChannelMessage = (event: MessageEvent<{ type?: string; generation?: number }>) => {
    if (event.data?.type !== 'logout') return;
    if (typeof event.data.generation === 'number' && event.data.generation > safeReadGeneration()) {
      safeWriteGeneration(event.data.generation);
    }
    this.handleRemoteLogout();
  };

  private readonly onStorage = (event: StorageEvent) => {
    if (event.key === AUTH_GENERATION_KEY && event.newValue !== event.oldValue) {
      this.handleRemoteLogout();
    }
  };
}
